import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Plus, Palette, Upload, Trash2, Edit3, X, Check, FileText, ArrowLeft, Star, Zap, ChevronDown, Eye, Monitor, Type, MessageSquare, Image, Layout, Globe, FileUp, BookOpen, Loader2, Target, Sparkles, Heart, Ban, Sliders, Share2, Link2, Copy, Users, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { authHeaders } from '../utils/authHeaders';
import { withBackend } from '../utils/api';
import GOOGLE_FONTS, { getFontCssUrl } from '../utils/googleFonts';

// ─── Semantic color role definitions ─────────────────────────────────────────
const COLOR_ROLES = [
  { key: 'color_background', label: 'Slide Background', desc: 'Main slide background', default: '#FFFFFF' },
  { key: 'color_surface', label: 'Card & Panel Fill', desc: 'Cards, boxes, containers', default: '#F8FAFC' },
  { key: 'color_text_primary', label: 'Headline Text', desc: 'Titles and headings', default: '#0F172A' },
  { key: 'color_text_body', label: 'Body Text', desc: 'Paragraphs and captions', default: '#475569' },
  { key: 'color_accent', label: 'Accent & Highlights', desc: 'Buttons, icons, callouts', default: '#6366F1' },
  { key: 'color_border', label: 'Borders & Dividers', desc: 'Lines, separators', default: '#E2E8F0' },
];

// ─── Tone dimension options ──────────────────────────────────────────────────
const TONE_DIMENSIONS = [
  { key: 'tone_formality', label: 'Formality', options: [
    { value: 'formal', label: 'Formal' },
    { value: 'balanced', label: 'Balanced' },
    { value: 'casual', label: 'Casual' },
  ]},
  { key: 'tone_perspective', label: 'Perspective', options: [
    { value: 'first', label: 'We / Our' },
    { value: 'second', label: 'You / Your' },
    { value: 'third', label: 'They / The company' },
  ]},
  { key: 'tone_complexity', label: 'Complexity', options: [
    { value: 'technical', label: 'Technical' },
    { value: 'accessible', label: 'Accessible' },
    { value: 'simple', label: 'Simple' },
  ]},
  { key: 'tone_sentence_style', label: 'Sentence Style', options: [
    { value: 'concise', label: 'Concise' },
    { value: 'standard', label: 'Standard' },
    { value: 'detailed', label: 'Detailed' },
  ]},
];

const LOGO_POSITIONS = [
  { value: 'top-left', label: 'Top Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
];

const LOGO_SHOW_OPTIONS = [
  { value: 'all', label: 'All slides' },
  { value: 'first-last', label: 'First & last' },
  { value: 'first', label: 'First only' },
  // r94: extraction can now learn this from the guidelines, and r89 made
  // the backend honor it — without the option here a prefilled 'none'
  // displayed as the first option while silently stripping every logo
  { value: 'none', label: 'No logo on slides' },
];

// ─── Tone Scale Axes ─────────────────────────────────────────────────────────
const TONE_SCALE_AXES = [
  { key: 'serious_playful', left: 'Serious', right: 'Playful' },
  { key: 'formal_casual', left: 'Formal', right: 'Casual' },
  { key: 'respectful_irreverent', left: 'Respectful', right: 'Irreverent' },
  { key: 'matter_of_fact_enthusiastic', left: 'Matter-of-fact', right: 'Enthusiastic' },
];

// ─── Design Element Keys ─────────────────────────────────────────────────────
const DESIGN_ELEMENT_KEYS = [
  { key: 'patterns', label: 'Patterns & Textures', placeholder: 'e.g. geometric lines, subtle gradients' },
  { key: 'icon_style', label: 'Icon Style', placeholder: 'e.g. outlined, filled, duotone' },
  { key: 'image_treatment', label: 'Image Treatment', placeholder: 'e.g. rounded corners, overlays, full-bleed' },
  { key: 'decorative_elements', label: 'Decorative Elements', placeholder: 'e.g. accent lines, shapes, frames' },
];

// ─── TagInput ────────────────────────────────────────────────────────────────
/**
 * r127: brand feedback memory — the user teaches the brand from finished
 * decks. Each rule ("do not use this shape on title slides", optional
 * screenshot of the misuse) is stored on the brand and inherited by every
 * future generation, the QA judge, and the evidence bundle.
 * Exists only for SAVED brands: rules attach to an id.
 */
function BrandFeedbackSection({ identityId }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [scope, setScope] = useState('');
  const [shot, setShot] = useState(null);      // {name, dataUrl}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const res = await fetch(withBackend(`/api/brand-identities/${identityId}/feedback`),
          { headers: await authHeaders() });
        const data = await res.json();
        if (!dead && res.ok) setRules(Array.isArray(data.rules) ? data.rules : []);
      } catch { /* section is additive; a fetch failure just shows empty */ }
      finally { if (!dead) setLoading(false); }
    })();
    return () => { dead = true; };
  }, [identityId]);

  const addRule = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const res = await fetch(withBackend(`/api/brand-identities/${identityId}/feedback`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          instruction: text.trim(),
          scope: scope.trim(),
          image_data_url: shot?.dataUrl || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save the rule');
      setRules(data.rules || []);
      setText(''); setScope(''); setShot(null);
      toast.success('Rule saved — every future generation with this brand will follow it.');
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  const removeRule = async (rid) => {
    try {
      const res = await fetch(
        withBackend(`/api/brand-identities/${identityId}/feedback/${rid}`),
        { method: 'DELETE', headers: await authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not remove the rule');
      setRules(data.rules || []);
    } catch (e) { toast.error(e.message); }
  };

  // r130: shared by the file picker, drag-and-drop and clipboard paste
  const takeShot = (f) => {
    // exactly the types the backend stores — a GIF/SVG would preview here,
    // toast success, and silently store image_url: null
    if (!f || !['image/png', 'image/jpeg', 'image/webp'].includes(f.type)) return;
    if (f.size > 4 * 1024 * 1024) { toast.error('Screenshot too large (4MB max)'); return; }
    const r = new FileReader();
    r.onload = () => setShot({ name: f.name || 'pasted-image', dataUrl: r.result });
    r.readAsDataURL(f);
  };
  const onShotPick = (e) => {
    takeShot(e.target.files?.[0]);
    e.target.value = '';
  };
  const onShotPaste = (e) => {
    const item = Array.from(e.clipboardData?.items || []).find((i) => i.type?.startsWith('image/'));
    const f = item?.getAsFile?.();
    if (f) { e.preventDefault(); takeShot(f); }
  };
  const shotDropProps = {
    onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); },
    onDrop: (e) => { e.preventDefault(); e.stopPropagation(); takeShot(e.dataTransfer?.files?.[0]); },
  };

  return (
    <div className="border border-border rounded-xl p-5 space-y-4" onPaste={onShotPaste}>
      <div>
        <h3 className="text-sm font-medium text-foreground">Teach this brand</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Spotted something a generated deck got wrong? Record the correction here —
          with a screenshot if you have one — and every future deck, and the quality
          check, will follow it.
        </p>
      </div>

      {loading ? (
        <p className="text-xs text-muted-foreground">Loading rules…</p>
      ) : rules.length > 0 && (
        <ul className="space-y-2">
          {rules.map((r) => (
            <li key={r.id} className="flex items-start gap-3 text-sm bg-muted/30 rounded-lg px-3 py-2">
              {r.image_url && (
                <img src={r.image_url} alt="" className="w-14 h-9 object-cover rounded border border-border shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-foreground">{r.instruction}</p>
                {r.scope && <p className="text-[11px] text-muted-foreground">Applies to: {r.scope}</p>}
              </div>
              <button
                onClick={() => removeRule(r.id)}
                className="text-muted-foreground hover:text-red-500 shrink-0"
                title="Remove this rule"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={'e.g. "Never use the hexagon shape on title slides" or "The tagline is always sentence case"'}
          rows={2}
          maxLength={500}
          className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
        />
        <div className="flex items-center gap-2">
          <input
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            placeholder="Applies to (optional): e.g. title slides"
            maxLength={120}
            className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-border bg-card text-foreground focus:outline-none"
          />
          <label {...shotDropProps} className="text-xs px-3 py-1.5 rounded-lg border border-border cursor-pointer hover:bg-muted/50 text-muted-foreground" title="Click, drop a file, or paste an image anywhere in this section">
            {shot ? shot.name.slice(0, 18) : 'Screenshot…'}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onShotPick} />
          </label>
          <Button size="sm" onClick={addRule} disabled={busy || !text.trim()}>
            {busy ? 'Saving…' : 'Add rule'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TagInput({ value = [], onChange, placeholder = 'Type and press Enter', colorClass = '' }) {
  const [input, setInput] = useState('');

  const addTag = (tag) => {
    const trimmed = tag.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInput('');
  };

  const removeTag = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(input);
    } else if (e.key === 'Backspace' && !input && value.length > 0) {
      removeTag(value.length - 1);
    }
  };

  return (
    <div className="min-h-[38px] flex flex-wrap gap-1.5 items-center rounded-lg border border-input bg-background px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-ring">
      {value.map((tag, i) => (
        <span key={i} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md ${colorClass || 'bg-primary/10 text-primary border border-primary/20'}`}>
          {tag}
          <button type="button" onClick={() => removeTag(i)} className="hover:text-destructive">
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => input && addTag(input)}
        placeholder={value.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[100px] bg-transparent text-sm outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
}

// ─── ToneScaleSlider ─────────────────────────────────────────────────────────
function ToneScaleSlider({ scales = {}, onChange }) {
  const handleChange = (key, val) => {
    onChange({ ...scales, [key]: parseFloat(val) });
  };

  return (
    <div className="space-y-3">
      {TONE_SCALE_AXES.map((axis) => {
        const val = scales[axis.key] ?? 0.5;
        return (
          <div key={axis.key} className="space-y-1">
            <div className="flex justify-between text-[10px] text-muted-foreground font-medium">
              <span>{axis.left}</span>
              <span>{axis.right}</span>
            </div>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={val}
              onChange={(e) => handleChange(axis.key, e.target.value)}
              className="w-full accent-primary"
            />
            <div className="text-center text-[10px] text-muted-foreground">
              {val <= 0.3 ? axis.left : val >= 0.7 ? axis.right : 'Balanced'}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── DesignElementsEditor ────────────────────────────────────────────────────
function DesignElementsEditor({ elements = {}, onChange }) {
  const handleChange = (key, val) => {
    onChange({ ...elements, [key]: val });
  };

  return (
    <div className="space-y-2.5">
      {DESIGN_ELEMENT_KEYS.map((item) => (
        <div key={item.key} className="space-y-1">
          <label className="text-[10px] font-medium text-muted-foreground">{item.label}</label>
          <Input
            value={elements[item.key] || ''}
            onChange={(e) => handleChange(item.key, e.target.value)}
            placeholder={item.placeholder}
            className="h-8 text-xs"
          />
        </div>
      ))}
    </div>
  );
}

// ─── ColorInput ──────────────────────────────────────────────────────────────
function ColorInput({ label, desc, value, onChange, id }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-lg border border-border cursor-pointer shadow-sm flex-shrink-0 transition-transform hover:scale-105"
          style={{ backgroundColor: value || '#e5e7eb' }}
          onClick={() => document.getElementById(`color-${id}`)?.click()}
        />
        <input
          id={`color-${id}`}
          type="color"
          value={value || '#6366F1'}
          onChange={(e) => onChange(e.target.value)}
          className="sr-only"
        />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-foreground">{label}</div>
          <div className="text-[10px] text-muted-foreground truncate">{desc}</div>
        </div>
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="not set"
          className="h-7 w-24 text-xs font-mono"
        />
      </div>
    </div>
  );
}

// ─── FontPicker with autocomplete + custom upload ───────────────────────────
function FontPicker({ label, value, slug, onChangeFont, onChangeSlug, customFonts = [], onUploadFont }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [loadedFonts, setLoadedFonts] = useState(new Set());
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // r96: the displayed text is LOCAL state seeded once at mount — when
  // extraction fills the form afterwards, the value prop changed but the
  // box kept showing empty (the user's 966 Innovation Hub ingest extracted
  // fonts that never appeared). Track external changes.
  useEffect(() => { setQuery(value || ''); }, [value]);
  const uploadRef = useRef(null);

  // Merge custom fonts with Google Fonts
  const allFonts = useMemo(() => {
    const custom = customFonts.map(cf => ({ name: cf.name, slug: `custom:${cf.name}`, category: 'custom', isCustom: true }));
    return [...custom, ...GOOGLE_FONTS];
  }, [customFonts]);

  // Filter fonts by query — show ALL matching fonts (the dropdown scrolls and
  // we lazy-load CSS so there's no performance hit).
  const filtered = useMemo(() => {
    if (!query) return allFonts;
    const q = query.toLowerCase();
    return allFonts.filter(f => f.name.toLowerCase().includes(q));
  }, [query, allFonts]);

  // Load font CSS for preview
  const loadFont = useCallback((fontSlug) => {
    if (loadedFonts.has(fontSlug)) return;
    const link = document.createElement('link');
    link.href = getFontCssUrl(fontSlug);
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    setLoadedFonts(prev => new Set(prev).add(fontSlug));
  }, [loadedFonts]);

  // Load only the first batch of fonts immediately when the dropdown opens
  // so we don't hit Google Fonts with hundreds of requests at once.
  useEffect(() => {
    if (open) {
      filtered.slice(0, 30).forEach(f => loadFont(f.slug));
    }
  }, [open, filtered, loadFont]);

  // Lazy-load font CSS as the user scrolls — each button registers with an
  // IntersectionObserver that fires loadFont() when the button becomes visible.
  const observerRef = useRef(null);
  useEffect(() => {
    if (!open || !dropdownRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const slug = entry.target.dataset.fontSlug;
            if (slug) loadFont(slug);
          }
        });
      },
      { root: dropdownRef.current, rootMargin: '200px' }
    );
    observerRef.current = obs;
    // Observe all font buttons currently rendered
    dropdownRef.current.querySelectorAll('[data-font-slug]').forEach((el) => obs.observe(el));
    return () => { obs.disconnect(); observerRef.current = null; };
  }, [open, filtered, loadFont]);

  // Load the selected font
  useEffect(() => {
    if (slug) loadFont(slug);
  }, [slug, loadFont]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectFont = (font) => {
    onChangeFont(font.name);
    onChangeSlug(font.slug);
    setQuery(font.name);
    setOpen(false);
  };

  return (
    <div className="space-y-1.5 relative">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <Input
        ref={inputRef}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onChangeFont(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search fonts..."
        className="h-9 text-sm"
      />
      {/* Preview line */}
      {value && (
        <p className="text-xs text-muted-foreground mt-1 truncate" style={{ fontFamily: `'${value}', sans-serif` }}>
          The quick brown fox jumps over the lazy dog
        </p>
      )}
      {/* Dropdown */}
      {open && (
        <div ref={dropdownRef} className="absolute z-50 top-full mt-1 w-full bg-popover border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {/* Upload custom font option */}
          {onUploadFont && (
            <button
              onClick={() => uploadRef.current?.click()}
              className="w-full text-left px-3 py-2 hover:bg-accent/50 transition-colors flex items-center gap-2 border-b border-border"
            >
              <Upload className="w-3.5 h-3.5 text-primary" />
              <span className="text-sm text-primary font-medium">Upload custom font...</span>
              <span className="text-[10px] text-muted-foreground ml-auto">.woff2 / .ttf / .otf</span>
            </button>
          )}
          {filtered.map((font) => (
            <button
              key={font.slug}
              data-font-slug={font.isCustom ? undefined : font.slug}
              onClick={() => selectFont(font)}
              className="w-full text-left px-3 py-2 hover:bg-accent/50 transition-colors flex items-center justify-between group"
            >
              <span className="text-sm" style={{ fontFamily: font.isCustom ? `'${font.name}'` : `'${font.name}', sans-serif` }}>
                {font.name}
              </span>
              <span className={`text-[10px] ${font.isCustom ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                {font.isCustom ? 'custom' : font.category}
              </span>
            </button>
          ))}
        </div>
      )}
      <input
        ref={uploadRef}
        type="file"
        accept=".woff2,.woff,.ttf,.otf"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && onUploadFont) {
            onUploadFont(file);
            setOpen(false);
          }
          e.target.value = '';
        }}
      />
    </div>
  );
}

// ─── ToneDimensionPicker ─────────────────────────────────────────────────────
function ToneDimensionPicker({ dimension, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">{dimension.label}</label>
      <div className="flex gap-1">
        {dimension.options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`flex-1 text-xs py-1.5 px-2 rounded-md border transition-all ${
              value === opt.value
                ? 'bg-primary/10 text-primary border-primary/30 font-medium'
                : 'border-border text-muted-foreground hover:bg-muted'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Extraction Tabs ─────────────────────────────────────────────────────────
const EXTRACT_TABS = [
  { key: 'manual', label: 'Manual', icon: Edit3 },
  { key: 'url', label: 'From Website', icon: Globe },
  { key: 'slides', label: 'From Slides', icon: FileUp },
  { key: 'guidelines', label: 'From Guidelines', icon: BookOpen },
];

function AnalysisOverlay({ visible, message }) {
  if (!visible) return null;
  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
      <div className="flex flex-col items-center gap-4 text-center max-w-sm px-6">
        <Loader2 className="w-10 h-10 text-primary animate-spin" />
        <p className="text-lg font-medium text-foreground">{message || 'Analyzing...'}</p>
        <p className="text-sm text-muted-foreground">This might take a few minutes for large files.</p>
      </div>
    </div>
  );
}

function ExtractionPanel({ mode, onExtracted }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const slidesFileRef = useRef(null);
  const [slidesLoading, setSlidesLoading] = useState(false);
  const [slidesFile, setSlidesFile] = useState(null);
  const [overlayMessage, setOverlayMessage] = useState('');
  // Which model analyzes the uploaded brand guidelines: 'gemini' (fast, default)
  // or 'opus' (Claude Opus 5, higher accuracy on tone/rules). Sent to the
  // extract endpoint; brand analysis runs once per brand so Opus cost is minor.
  const [brandModel, setBrandModel] = useState('gemini');

  const handleUrlExtract = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setOverlayMessage(`Analyzing ${url.trim().split('/').pop() || 'website'}...`);
    setError(null);
    try {
      const res = await fetch(withBackend('/api/brand-identities/extract-from-url'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Extraction failed');
      onExtracted(data.extracted);
      toast.success(`Extracted brand from ${data.extracted.brand_name || url}`);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
      setOverlayMessage('');
    }
  };

  const handleFileExtract = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setOverlayMessage(`Analyzing ${file.name}...`);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('model', brandModel);
      const endpoint = mode === 'slides'
        ? '/api/brand-identities/extract-from-slides'
        : '/api/brand-identities/extract-from-guidelines';
      const res = await fetch(withBackend(endpoint), { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Extraction failed');
      onExtracted(data.extracted);
      toast.success(`Extracted brand from ${file.name}`);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
      setOverlayMessage('');
    }
  };

  if (mode === 'url') {
    return (
      <>
      <AnalysisOverlay visible={loading} message={overlayMessage} />
      <div className="space-y-4 p-6 bg-muted/30 rounded-xl border border-border">
        <div className="flex items-center gap-2 mb-1">
          <Globe className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-medium text-foreground">Extract from Website</h3>
        </div>
        <p className="text-xs text-muted-foreground">Enter a company website URL. We'll analyze the CSS, fonts, colors, and content to pre-fill your brand identity.</p>
        <div className="flex gap-2">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com"
            className="flex-1 h-10"
            onKeyDown={(e) => e.key === 'Enter' && handleUrlExtract()}
            disabled={loading}
          />
          <Button onClick={handleUrlExtract} disabled={loading || !url.trim()} variant="gradient" className="px-6">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Extract'}
          </Button>
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
      </>
    );
  }

  if (mode === 'slides') {
    return (
      <>
      <AnalysisOverlay visible={loading} message={overlayMessage} />
      <div className="space-y-4 p-6 bg-muted/30 rounded-xl border border-border">
        <div className="flex items-center gap-2 mb-1">
          <FileUp className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-medium text-foreground">Extract from Slides</h3>
        </div>
        <p className="text-xs text-muted-foreground">Upload a PPTX or PDF with existing branded slides. We'll extract exact colors, fonts, and layout patterns.</p>
        <label
          className="cursor-pointer flex flex-col items-center gap-3 border-2 border-dashed border-border rounded-xl px-6 py-8 hover:border-primary/30 transition-colors"
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (!loading && e.dataTransfer?.files?.length) handleFileExtract({ target: { files: e.dataTransfer.files } }); }}
        >
          {loading ? (
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          ) : (
            <Upload className="w-8 h-8 text-muted-foreground" />
          )}
          <span className="text-sm text-muted-foreground">{loading ? 'Analyzing slides...' : 'Drop .pptx or .pdf here, or click to browse'}</span>
          <input ref={fileRef} type="file" accept=".pptx,.ppt,.pdf" onChange={handleFileExtract} className="sr-only" disabled={loading} />
        </label>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
      </>
    );
  }

  const handleIngestAll = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setLoading(true);
    setOverlayMessage(`Analyzing ${list.length} file(s) — classifying and extracting everything...`);
    setError(null);
    try {
      const formData = new FormData();
      list.forEach((f, i) => formData.append(`file_${i}`, f));
      formData.append('model', brandModel);

      const res = await fetch(withBackend('/api/brand-identities/ingest'), { method: 'POST', body: formData, headers: await authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Ingest failed');
      // hand the FONT FILES up too — the backend read their metadata, but
      // the save path needs the actual File objects to upload them
      const fontFiles = list.filter((f) => /\.(otf|ttf|woff2?)$/i.test(f.name));
      onExtracted(data.extracted || {}, { ...data, _font_files: fontFiles });
      const kinds = (data.files || []).map((f) => `${f.name} → ${f.kind}`).join(' · ');
      toast.success(`Analyzed ${data.files?.length || 0} file(s). ${kinds}`.slice(0, 220), { duration: 9000 });
      if (data.logo_candidates?.length) toast.info(`${data.logo_candidates.length} logo candidate(s) found — confirm them next to the logo uploads below.`, { duration: 9000 });
      if (data.gaps?.length) toast.info(`Not found in the files: ${data.gaps.join(', ')} — fill manually or add more files.`, { duration: 9000 });
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
      setOverlayMessage('');
      e.target.value = '';
    }
  };

  if (mode === 'guidelines') {
    const handleSlidesExtract = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setSlidesFile(file);
      setSlidesLoading(true);
      setOverlayMessage(`Analyzing slide patterns from ${file.name}...`);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(withBackend('/api/brand-identities/extract-from-slides'), { method: 'POST', body: formData });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Slide extraction failed');
        // Only apply design_patterns from slides — don't override colors/fonts from guidelines
        if (data.extracted?.design_patterns) {
          onExtracted({ design_patterns: data.extracted.design_patterns });
          toast.success('Layout patterns extracted from slides');
        } else {
          toast.info('No layout patterns detected — try uploading more slides');
        }
      } catch (err) {
        toast.error(err.message);
      } finally {
        setSlidesLoading(false);
        setOverlayMessage('');
      }
    };

    return (
      <>
      <AnalysisOverlay visible={loading || slidesLoading} message={overlayMessage} />
      <div className="space-y-4 p-6 bg-muted/30 rounded-xl border border-border">
        {/* ── r95: the scenario's front door — everything at once ── */}
        <div
          className="p-4 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5"
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (!loading && e.dataTransfer?.files?.length) handleIngestAll({ target: { files: e.dataTransfer.files } }); }}
        >
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-medium text-foreground">Add all your brand files at once</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            Guidelines, existing decks, logo images, font files — any mix. We work out what each
            file is and extract everything: stated rules win, deck observations fill the gaps,
            and logos are pulled from the files themselves.
          </p>
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium cursor-pointer hover:opacity-90 transition-opacity">
            <Upload className="w-3.5 h-3.5" />
            Choose brand files
            <input type="file" multiple className="hidden" disabled={loading}
              accept=".pdf,.pptx,.ppt,.png,.jpg,.jpeg,.svg,.webp,.otf,.ttf,.woff,.woff2"
              onChange={handleIngestAll} />
          </label>
        </div>

        <div className="flex items-center gap-2 mb-1">
          <BookOpen className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-medium text-foreground">Or: extract from Brand Guidelines only</h3>
        </div>
        <p className="text-xs text-muted-foreground">Upload a brand guidelines PDF. We'll analyze color swatches, font specimens, logo rules, and tone guidelines page by page.</p>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Analysis model:</span>
          {[
            { value: 'gemini', label: 'Gemini Pro', hint: 'Fast, great vision (default)' },
            { value: 'opus', label: 'Opus 5', hint: 'Slower, higher accuracy on tone & rules' },
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setBrandModel(opt.value)}
              disabled={loading}
              title={opt.hint}
              className={`px-2.5 py-1 rounded-lg border transition-colors disabled:opacity-50 ${brandModel === opt.value ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border text-muted-foreground hover:text-foreground'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <label
          className="cursor-pointer flex flex-col items-center gap-3 border-2 border-dashed border-border rounded-xl px-6 py-8 hover:border-primary/30 transition-colors"
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (!loading && e.dataTransfer?.files?.length) handleFileExtract({ target: { files: e.dataTransfer.files } }); }}
        >
          {loading ? (
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          ) : (
            <Upload className="w-8 h-8 text-muted-foreground" />
          )}
          <span className="text-sm text-muted-foreground">{loading ? `Analyzing guidelines PDF with ${brandModel === 'opus' ? 'Opus 5' : 'Gemini Pro'}...` : 'Drop brand guidelines PDF here, or click to browse'}</span>
          <input ref={fileRef} type="file" accept=".pdf" onChange={handleFileExtract} className="sr-only" disabled={loading} />
        </label>

        {/* Optional: also upload reference slides for layout pattern extraction */}
        <div className="pt-3 border-t border-border/50">
          <div className="flex items-center gap-2 mb-1">
            <FileUp className="w-4 h-4 text-primary/70" />
            <h4 className="text-xs font-medium text-foreground">Reference Slides (optional)</h4>
          </div>
          <p className="text-xs text-muted-foreground mb-2">Upload a PPTX or PDF with sample branded slides. We'll extract layout patterns and composition rules to complement the guidelines.</p>
          {slidesFile ? (
            <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-lg px-3 py-2">
              <FileText className="w-4 h-4 text-primary" />
              <span className="text-xs text-foreground flex-1">{slidesFile.name}</span>
              {slidesLoading && <Loader2 className="w-3.5 h-3.5 text-primary animate-spin" />}
            </div>
          ) : (
            <label
              className="cursor-pointer flex items-center gap-2 border-2 border-dashed border-border rounded-lg px-4 py-2.5 hover:border-primary/30 transition-colors"
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (!slidesLoading && e.dataTransfer?.files?.length) handleSlidesExtract({ target: { files: e.dataTransfer.files } }); }}
            >
              <Upload className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Drop or upload .pptx/.pdf with sample slides</span>
              <input ref={slidesFileRef} type="file" accept=".pptx,.ppt,.pdf" onChange={handleSlidesExtract} className="sr-only" disabled={slidesLoading} />
            </label>
          )}
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
      </>
    );
  }

  return null;
}

// ─── Main Form ───────────────────────────────────────────────────────────────
function BrandIdentityForm({ identity, onSave, onCancel, userId }) {
  const [form, setForm] = useState(() => {
    const i = identity || {};
    return {
      name: i.name || '',
      // Legacy colors (still sent for backwards compat)
      primary_color: i.primary_color || '#6366F1',
      secondary_color: i.secondary_color || '#A855F7',
      accent_color: i.accent_color || '#EC4899',
      // Semantic colors
      // (was `i.color_background || i.primary_color ? '' : ''` — precedence
      // made that ALWAYS '' — so opening the edit form silently wiped the
      // saved background and the next save persisted the wipe.)
      color_background: i.color_background || '',
      color_surface: i.color_surface || '',
      color_text_primary: i.color_text_primary || '',
      color_text_body: i.color_text_body || '',
      color_accent: i.color_accent || '',
      color_border: i.color_border || '',
      // Fonts
      font_heading: i.font_heading || '',
      font_body: i.font_body || '',
      font_heading_slug: i.font_heading_slug || '',
      font_body_slug: i.font_body_slug || '',
      font_heading_ar: i.font_heading_ar || '',
      font_body_ar: i.font_body_ar || '',
      font_heading_ar_slug: i.font_heading_ar_slug || '',
      font_body_ar_slug: i.font_body_ar_slug || '',
      brand_motifs: (() => {
        try {
          const m = typeof i.brand_motifs === 'string' ? JSON.parse(i.brand_motifs) : i.brand_motifs;
          return Array.isArray(m) ? m : [];
        } catch { return []; }
      })(),
      // Tone
      tone_formality: i.tone_formality || 'balanced',
      tone_perspective: i.tone_perspective || 'second',
      tone_complexity: i.tone_complexity || 'accessible',
      tone_sentence_style: i.tone_sentence_style || 'standard',
      tone_of_voice: i.tone_of_voice || '',
      // Logo placement
      // 'bottom-right' matches the backend injector's assumption — the old
      // 'top-left' here made the form disagree with what decks actually did
      logo_position: i.logo_position || 'bottom-right',
      logo_max_width: i.logo_max_width || 150,
      logo_show_on: i.logo_show_on || 'all',
      // r170: prose is the source of truth for logo usage. Seed from the
      // harvested guidelines notes (design_elements.logo_notes) when the
      // column is still empty — first save promotes it to the column.
      // r181: company fact sheet (proposal authoritative tier). Arrives as a
      // JSONB array (or a JSON string from older rows); rides back to the
      // backend as a JSON string via the generic array serializer below.
      fact_sheet: (() => {
        try {
          const v = typeof i.fact_sheet === 'string' ? JSON.parse(i.fact_sheet) : i.fact_sheet;
          return Array.isArray(v) ? v : [];
        } catch { return []; }
      })(),
      logo_usage: i.logo_usage || (() => {
        try {
          const de = typeof i.design_elements === 'string' ? JSON.parse(i.design_elements) : i.design_elements;
          return (de && de.logo_notes) || '';
        } catch { return ''; }
      })(),
      // Brand identity & positioning
      brand_personality: i.brand_personality || [],
      brand_values: i.brand_values || [],
      mission_statement: i.mission_statement || '',
      tagline: i.tagline || '',
      target_audience: i.target_audience || '',
      // Visual style
      visual_mood: i.visual_mood || '',
      style_guidelines: i.style_guidelines || '',
      design_elements: (typeof i.design_elements === 'string' ? (() => { try { return JSON.parse(i.design_elements); } catch { return {}; } })() : i.design_elements) || {},
      // Tone scales & content
      tone_scales: (typeof i.tone_scales === 'string' ? (() => { try { return JSON.parse(i.tone_scales); } catch { return {}; } })() : i.tone_scales) || {},
      content_guidelines: i.content_guidelines || '',
      words_to_use: i.words_to_use || [],
      words_to_avoid: i.words_to_avoid || [],
      // Layout & composition patterns (extracted from reference slides via vision analysis)
      design_patterns: i.design_patterns || '',
    };
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(identity?.logo_url || null);
  const [logoReversedFile, setLogoReversedFile] = useState(null);
  const [logoReversedPreview, setLogoReversedPreview] = useState(identity?.logo_reversed_url || null);
  const [logoIconFile, setLogoIconFile] = useState(null);
  const [logoIconPreview, setLogoIconPreview] = useState(identity?.logo_icon_url || null);
  // r95: logos pulled from the ingested FILES, awaiting one-click confirm
  const [logoCandidates, setLogoCandidates] = useState([]);

  // r96: DRAFT AUTOSAVE for new-brand forms. The user filled a rich form
  // via a multi-minute extraction, closed it accidentally, and lost
  // everything — the form now persists locally as it changes and offers a
  // restore on reopen. New identities only (edits have a saved row);
  // cleared ONLY on successful save (cancel is exactly the accident case).
  const DRAFT_KEY = 'wizardly_brand_draft_new';
  const [draftAvailable, setDraftAvailable] = useState(() => {
    if (identity?.id) return null;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      const d = JSON.parse(raw);
      return (d?.form?.name || '').trim() ? d : null;
    } catch { return null; }
  });
  useEffect(() => {
    if (identity?.id) return;
    const t = setTimeout(() => {
      try {
        if ((form.name || '').trim()) {
          localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, savedAt: Date.now() }));
        }
      } catch { /* storage full — draft simply not kept */ }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, identity?.id]);
  const [refDocFile, setRefDocFile] = useState(null);
  const [refDocName, setRefDocName] = useState(identity?.reference_doc_url ? 'Uploaded' : null);
  const [refSlidesFile, setRefSlidesFile] = useState(null);
  const [refSlidesPreviews, setRefSlidesPreviews] = useState(() => {
    const slides = identity?.reference_slides;
    if (!slides) return [];
    const parsed = typeof slides === 'string' ? JSON.parse(slides) : slides;
    return Array.isArray(parsed) ? parsed : [];
  });
  const [saving, setSaving] = useState(false);
  const [showMigrationBanner, setShowMigrationBanner] = useState(false);
  const [activeTab, setActiveTab] = useState('manual');
  const [customFonts, setCustomFonts] = useState([]); // { name, url, file }
  const [uploadingFont, setUploadingFont] = useState(false);

  // Apply extracted brand data to form
  const applyExtracted = useCallback((extracted, full) => {
    // r95 ingest extras: logo candidates for the confirm strip, and the
    // font FILES so the existing save path uploads them
    if (full?.logo_candidates?.length) setLogoCandidates(full.logo_candidates);
    // r101: reference pages the ingest already persisted (slides + guideline
    // pages). They ride through save as URLs so generation keeps seeing the
    // brand's own material — the strongest brand signal we have.
    if (extracted?.reference_slides_prebuilt?.length) {
      const _n = extracted.reference_slides_prebuilt.length;
      toast.info(`${_n} reference page(s) saved with this brand — the generator will use them as visual reference.`, { duration: 8000 });
    }
    if (full?._font_files?.length) {
      setCustomFonts(prev => [...prev, ...full._font_files.map(f => ({ file: f, name: f.name.replace(/\.[^.]+$/, '') }))]);
    }
    setForm(f => {
      // Parse JSON fields if they come as strings
      let toneScales = extracted.tone_scales || f.tone_scales;
      if (typeof toneScales === 'string') { try { toneScales = JSON.parse(toneScales); } catch { toneScales = f.tone_scales; } }
      let designElems = extracted.design_elements || f.design_elements;
      if (typeof designElems === 'string') { try { designElems = JSON.parse(designElems); } catch { designElems = f.design_elements; } }

      return {
        ...f,
        name: extracted.brand_name || f.name,
        color_background: extracted.color_background || f.color_background,
        color_surface: extracted.color_surface || f.color_surface,
        color_text_primary: extracted.color_text_primary || f.color_text_primary,
        color_text_body: extracted.color_text_body || f.color_text_body,
        color_accent: extracted.color_accent || f.color_accent,
        color_border: extracted.color_border || f.color_border,
        font_heading: extracted.font_heading || f.font_heading,
        font_body: extracted.font_body || f.font_body,
        // r94: the new extraction asks land in the form too — Arabic pair
        // and the guidelines' own logo behavior (position + which slides)
        font_heading_ar: extracted.font_heading_ar || f.font_heading_ar,
        font_body_ar: extracted.font_body_ar || f.font_body_ar,
        logo_position: extracted.logo_position || f.logo_position,
        logo_show_on: extracted.logo_show_on || f.logo_show_on,
        // r170: the guidelines' own logo-usage prose lands in the textarea
        logo_usage: extracted.logo_usage || (() => {
          try {
            const de = typeof extracted.design_elements === 'string' ? JSON.parse(extracted.design_elements) : extracted.design_elements;
            return (de && de.logo_notes) || f.logo_usage;
          } catch { return f.logo_usage; }
        })(),
        reference_slides_prebuilt: extracted.reference_slides_prebuilt || f.reference_slides_prebuilt,
        tone_formality: extracted.tone_formality || f.tone_formality,
        tone_perspective: extracted.tone_perspective || f.tone_perspective,
        tone_complexity: extracted.tone_complexity || f.tone_complexity,
        tone_sentence_style: extracted.tone_sentence_style || f.tone_sentence_style,
        tone_of_voice: extracted.tone_of_voice || f.tone_of_voice,
        // Extended fields
        brand_personality: extracted.brand_personality?.length ? extracted.brand_personality : f.brand_personality,
        brand_values: extracted.brand_values?.length ? extracted.brand_values : f.brand_values,
        mission_statement: extracted.mission_statement || f.mission_statement,
        tagline: extracted.tagline || f.tagline,
        target_audience: extracted.target_audience || f.target_audience,
        visual_mood: extracted.visual_mood || f.visual_mood,
        tone_scales: (toneScales && Object.keys(toneScales).length > 0) ? toneScales : f.tone_scales,
        content_guidelines: extracted.content_guidelines || f.content_guidelines,
        style_guidelines: extracted.style_guidelines || f.style_guidelines,
        // MERGE extracted design elements over the existing ones — a
        // re-extraction only produces the 4 prose keys, and replacing the
        // whole object silently wiped the machine config the backend also
        // stores here (motif_policy / font_classes / editorial_font).
        design_elements: (designElems && Object.keys(designElems).length > 0)
          ? { ...(f.design_elements || {}), ...designElems }
          : f.design_elements,
        words_to_use: extracted.words_to_use?.length ? extracted.words_to_use : f.words_to_use,
        words_to_avoid: extracted.words_to_avoid?.length ? extracted.words_to_avoid : f.words_to_avoid,
        design_patterns: extracted.design_patterns || f.design_patterns,
      };
    });
    setActiveTab('manual'); // Switch to manual to review
  }, []);

  // Show migration banner if editing existing identity with old colors but no semantic colors
  useEffect(() => {
    if (identity?.id && identity.primary_color && !identity.color_accent) {
      setShowMigrationBanner(true);
    }
  }, [identity]);

  const handleMigrate = () => {
    setForm(f => ({
      ...f,
      color_accent: f.primary_color || '#6366F1',
      color_surface: f.secondary_color || '#A855F7',
      color_border: f.accent_color || '#EC4899',
      color_background: '#FFFFFF',
      color_text_primary: '#0F172A',
      color_text_body: '#475569',
    }));
    setShowMigrationBanner(false);
    toast.success('Colors migrated to semantic roles — review and adjust');
  };

  const updateForm = (key, value) => setForm(f => ({ ...f, [key]: value }));

  // r181: company fact sheet editor (proposal authoritative tier). Entries are
  // approved by a PERSON — drafts from a past proposal arrive unapproved and
  // the backend never serves an unapproved or expired entry to a proposal.
  const FS_CATEGORIES = ['about_us', 'certificate', 'team_bio', 'rate_card', 'terms', 'methodology', 'case_study'];
  const fsNewEntry = () => ({
    id: `fs-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    category: 'about_us', title: '', body: '', approved: false, last_verified: '', expires: '', disclosure: 'nameable',
  });
  const updateFsEntry = (idx, patch) => setForm(f => ({ ...f, fact_sheet: (f.fact_sheet || []).map((e, i) => (i === idx ? { ...e, ...patch } : e)) }));
  const removeFsEntry = (idx) => setForm(f => ({ ...f, fact_sheet: (f.fact_sheet || []).filter((_, i) => i !== idx) }));
  const [fsSeedText, setFsSeedText] = useState('');
  const [fsSeeding, setFsSeeding] = useState(false);

  // r182: past-proposal reference library (one cached extraction per document).
  // Needs a saved brand id; the list refreshes whenever the brand changes.
  const [pcCards, setPcCards] = useState([]);
  const [pcUploading, setPcUploading] = useState(false);
  const loadProposalCards = async (brandId) => {
    if (!brandId) { setPcCards([]); return; }
    try {
      const res = await fetch(withBackend(`/api/brand-identities/${brandId}/proposal-cards`), { headers: await authHeaders() });
      const data = res.ok ? await res.json() : null;
      setPcCards(Array.isArray(data?.cards) ? data.cards : []);
    } catch { setPcCards([]); }
  };
  useEffect(() => { loadProposalCards(identity?.id); }, [identity?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const uploadProposalCards = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!identity?.id || !list.length) return;
    setPcUploading(true);
    let added = 0;
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(withBackend(`/api/brand-identities/${identity.id}/proposal-cards`), { method: 'POST', body: fd, headers: await authHeaders() });
        const data = await res.json().catch(() => null);
        if (!res.ok) { toast.error(`${file.name}: ${data?.error || 'could not be added'}`); continue; }
        added += 1;
        if (data?.cached) toast.info(`${file.name} was already in the library.`);
      }
      if (added) toast.success(`${added} past proposal${added === 1 ? '' : 's'} added to the reference library.`);
    } finally {
      setPcUploading(false);
      loadProposalCards(identity?.id);
    }
  };
  const removeProposalCard = async (cardId) => {
    if (!identity?.id) return;
    try {
      const res = await fetch(withBackend(`/api/brand-identities/${identity.id}/proposal-cards/${cardId}`), { method: 'DELETE', headers: await authHeaders() });
      if (!res.ok) { toast.error('Could not remove that proposal.'); return; }
      setPcCards(prev => prev.filter(c => c.id !== cardId));
    } catch { toast.error('Could not remove that proposal.'); }
  };
  const seedFactSheet = async () => {
    const text = fsSeedText.trim();
    if (!text) return;
    setFsSeeding(true);
    try {
      const res = await fetch(withBackend('/api/proposal/fact-sheet/draft'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ text }),
      });
      const data = res.ok ? await res.json() : null;
      const drafts = (data?.entries || []).map(e => ({ ...fsNewEntry(), ...e, approved: false }));
      if (!drafts.length) { toast.error(data?.error || 'No facts could be drafted from that text. The sheet is unchanged.'); return; }
      setForm(f => ({ ...f, fact_sheet: [...(f.fact_sheet || []), ...drafts] }));
      setFsSeedText('');
      toast.success(`${drafts.length} draft fact${drafts.length === 1 ? '' : 's'} added for review. Tick Approved on the ones a proposal may assert.`);
    } catch {
      toast.error('Drafting facts failed. The sheet is unchanged.');
    } finally {
      setFsSeeding(false);
    }
  };

  const handleUploadFont = useCallback(async (file) => {
    const fontName = file.name.replace(/\.(woff2?|ttf|otf)$/i, '').replace(/[-_]/g, ' ');
    const localUrl = URL.createObjectURL(file);

    // Register @font-face locally for preview
    const style = document.createElement('style');
    style.textContent = `@font-face { font-family: '${fontName}'; src: url('${localUrl}'); font-display: swap; }`;
    document.head.appendChild(style);

    setCustomFonts(prev => [...prev, { name: fontName, url: localUrl, file }]);
    toast.success(`Font "${fontName}" loaded for preview`);
  }, []);

  const handleFileChange = (setter, previewSetter) => (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setter(file);
      previewSetter(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error('Brand name is required');
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('user_id', userId);

      // Sync legacy colors from semantic roles for backwards compat
      const submitForm = { ...form };
      if (submitForm.color_accent) submitForm.primary_color = submitForm.color_accent;
      if (submitForm.color_surface) submitForm.secondary_color = submitForm.color_surface;
      if (submitForm.color_border) submitForm.accent_color = submitForm.color_border;

      // Serialize arrays and objects for FormData transport
      const arrayFields = ['brand_personality', 'brand_values', 'words_to_use', 'words_to_avoid'];
      // r101: reference_slides_prebuilt rides as JSON — the ingest endpoint
      // already rendered AND uploaded those pages, so only their URLs travel.
      const jsonFields = ['tone_scales', 'design_elements', 'brand_motifs', 'reference_slides_prebuilt'];
      arrayFields.forEach(k => {
        if (Array.isArray(submitForm[k])) submitForm[k] = JSON.stringify(submitForm[k]);
      });
      jsonFields.forEach(k => {
        if (typeof submitForm[k] === 'object' && submitForm[k] !== null) submitForm[k] = JSON.stringify(submitForm[k]);
      });
      // Don't send an empty motifs array unless the brand actually HAD motifs
      // to clear — keeps brand edits working before the brand_motifs
      // migration has run (unknown column would 500 the whole update).
      if (submitForm.brand_motifs === '[]' && !identity?.brand_motifs) delete submitForm.brand_motifs;

      Object.entries(submitForm).forEach(([k, v]) => {
        // '' is a deliberate CLEAR — the backend accepts empty strings for
        // exactly this; the old `v !== ''` filter made bad values
        // permanent (clear looked saved, toast said success, field came back)
        if (v !== null && v !== undefined) formData.append(k, v);
      });

      if (logoFile) formData.append('logo', logoFile);
      if (logoReversedFile) formData.append('logo_reversed', logoReversedFile);
      if (logoIconFile) formData.append('logo_icon', logoIconFile);
      if (refDocFile) formData.append('reference_doc', refDocFile);
      if (refSlidesFile) formData.append('reference_slides', refSlidesFile);
      if (refSlidesPreviews.length === 0 && !refSlidesFile) formData.append('clear_reference_slides', 'true');

      // Attach custom font files
      customFonts.forEach((cf, i) => {
        if (cf.file) formData.append(`custom_font_${i}`, cf.file);
      });

      const url = identity?.id
        ? withBackend(`/api/brand-identities/${identity.id}`)
        : withBackend('/api/brand-identities/create');
      const method = identity?.id ? 'PATCH' : 'POST';

      // Reference-deck processing legitimately runs 30-90s; anything past
      // 3 minutes is a hang, and with Cancel disabled while saving the
      // timeout is the user's only exit.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000);
      let res;
      try {
        res = await fetch(url, { method, body: formData, signal: controller.signal, headers: await authHeaders() });
      } finally {
        clearTimeout(timeoutId);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      // clear the draft only when THIS save was the new-brand form —
      // handleSubmit is shared with edits, and an unrelated edit-save
      // silently destroyed a pending new-brand draft (r96 review P1)
      if (!identity?.id) {
        try { localStorage.removeItem('wizardly_brand_draft_new'); } catch { /* noop */ }
      }

      toast.success(identity?.id ? 'Brand identity updated' : 'Brand identity created');
      onSave(data.brand_identity);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-bold text-foreground">
          {identity?.id ? 'Edit Brand Identity' : 'New Brand Identity'}
        </h2>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          <X className="w-4 h-4 mr-1" /> Cancel
        </Button>
      </div>

      {/* ── Extraction tabs (only for new identities) ── */}
      {!identity?.id && (
        <div className="mb-6">
          <div className="flex gap-1 bg-muted/50 p-1 rounded-xl border border-border">
            {EXTRACT_TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    activeTab === tab.key
                      ? 'bg-background text-foreground shadow-sm border border-border/50'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Extraction panel ── */}
      {activeTab !== 'manual' && (
        <div className="mb-6">
          <ExtractionPanel mode={activeTab} onExtracted={applyExtracted} />
        </div>
      )}

      <div className="grid grid-cols-1">
        {/* ── Form (full width — the live slide-preview panel was removed) ── */}
        <div className="space-y-8">
          {/* r96: unsaved-draft restore — a closed tab must not cost an extraction */}
          {draftAvailable && (
            <div className="bg-primary/5 border border-primary/25 rounded-xl p-4 flex items-center justify-between gap-4">
              <p className="text-sm text-foreground min-w-0">
                You have an unsaved brand draft
                {draftAvailable.form?.name ? <> for <span className="font-medium">“{draftAvailable.form.name}”</span></> : null}
                {draftAvailable.savedAt ? ` from ${new Date(draftAvailable.savedAt).toLocaleString()}` : ''}.
                <span className="text-muted-foreground"> Logo and font file uploads aren’t kept in drafts — re-add those.</span>
              </p>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" onClick={() => {
                  setForm(f => ({ ...f, ...draftAvailable.form }));
                  setDraftAvailable(null);
                  toast.success('Draft restored');
                }}>Restore</Button>
                <Button size="sm" variant="outline" onClick={() => {
                  try { localStorage.removeItem('wizardly_brand_draft_new'); } catch { /* noop */ }
                  setDraftAvailable(null);
                }}>Discard</Button>
              </div>
            </div>
          )}
          {/* Migration banner */}
          {showMigrationBanner && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Migrate to semantic color roles</p>
                <p className="text-xs text-amber-600/80 dark:text-amber-400/70 mt-0.5">Your 3 colors will be mapped to the new 6-role system. You can adjust after.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setShowMigrationBanner(false)}>Skip</Button>
                <Button size="sm" onClick={handleMigrate} className="bg-amber-500 hover:bg-amber-600 text-white">Migrate</Button>
              </div>
            </div>
          )}

          {/* Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Brand Name *</label>
            <Input
              value={form.name}
              onChange={(e) => updateForm('name', e.target.value)}
              placeholder="e.g. Acme Corp"
              className="h-11"
            />
          </div>

          {/* ── Colors ── */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-foreground">Color Roles</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {COLOR_ROLES.map((role) => (
                <ColorInput
                  key={role.key}
                  id={role.key}
                  label={role.label}
                  desc={role.desc}
                  value={form[role.key]}
                  onChange={(v) => updateForm(role.key, v)}
                />
              ))}
              {/* r98b: the dual-tone fact was invisible plumbing — brands
                  with dark covers over light content pages (extracted or
                  hand-set) need to SEE and correct both grounds. Empty =
                  single-ground brand. */}
              <ColorInput
                id="dual_tone_cover_ground"
                label="Cover & Divider Background"
                desc="Dual-tone brands: dark ground for covers, dividers, TOC only — content slides keep the Slide Background. Leave empty for single-ground brands."
                value={(form.design_elements || {}).dual_tone_cover_ground || ''}
                onChange={(v) => {
                  const de = { ...(form.design_elements || {}) };
                  // Always write (even ''): the backend PATCH merges
                  // design_elements OVER the stored row, so a deleted key
                  // would silently resurrect — clearing became impossible
                  // through the UI (r98b review P1). '' reads as unset
                  // everywhere server-side.
                  de.dual_tone_cover_ground = v || '';
                  updateForm('design_elements', de);
                }}
              />
            </div>
            {/* Color strip preview */}
            <div className="flex gap-1 rounded-lg overflow-hidden h-6">
              {COLOR_ROLES.map((role) => (
                <div
                  key={role.key}
                  className="flex-1 transition-colors"
                  style={{ backgroundColor: form[role.key] || role.default }}
                  title={role.label}
                />
              ))}
              {(form.design_elements || {}).dual_tone_cover_ground && (
                <div
                  className="flex-1 transition-colors"
                  style={{ backgroundColor: (form.design_elements || {}).dual_tone_cover_ground }}
                  title="Cover & Divider Background"
                />
              )}
            </div>
          </div>

          {/* ── Typography ── */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Type className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-foreground">Typography</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FontPicker
                label="Heading Font"
                value={form.font_heading}
                slug={form.font_heading_slug}
                onChangeFont={(v) => updateForm('font_heading', v)}
                onChangeSlug={(v) => updateForm('font_heading_slug', v)}
                customFonts={customFonts}
                onUploadFont={handleUploadFont}
              />
              <FontPicker
                label="Body Font"
                value={form.font_body}
                slug={form.font_body_slug}
                onChangeFont={(v) => updateForm('font_body', v)}
                onChangeSlug={(v) => updateForm('font_body_slug', v)}
                customFonts={customFonts}
                onUploadFont={handleUploadFont}
              />
              <FontPicker
                label="Arabic Heading Font"
                value={form.font_heading_ar}
                slug={form.font_heading_ar_slug}
                onChangeFont={(v) => updateForm('font_heading_ar', v)}
                onChangeSlug={(v) => updateForm('font_heading_ar_slug', v)}
                customFonts={customFonts}
                onUploadFont={handleUploadFont}
              />
              <FontPicker
                label="Arabic Body Font"
                value={form.font_body_ar}
                slug={form.font_body_ar_slug}
                onChangeFont={(v) => updateForm('font_body_ar', v)}
                onChangeSlug={(v) => updateForm('font_body_ar_slug', v)}
                customFonts={customFonts}
                onUploadFont={handleUploadFont}
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Arabic fonts are optional — set them so Arabic decks and documents use your brand's Arabic
              faces (e.g. Cairo, Tajawal, Almarai) instead of generic fallbacks. Latin content keeps the
              primary fonts.
            </p>
          </div>

          {/* ── Tone of Voice ── */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-foreground">Tone of Voice</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {TONE_DIMENSIONS.map((dim) => (
                <ToneDimensionPicker
                  key={dim.key}
                  dimension={dim}
                  value={form[dim.key]}
                  onChange={(v) => updateForm(dim.key, v)}
                />
              ))}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Additional tone notes (optional)</label>
              <textarea
                value={form.tone_of_voice}
                onChange={(e) => updateForm('tone_of_voice', e.target.value)}
                placeholder="e.g. Avoid jargon. Use active voice. Keep sentences short."
                rows={2}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>
          </div>

          {/* ── Brand Identity & Positioning ── */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-foreground">Brand Identity & Positioning</h3>
            </div>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Tagline / Slogan</label>
                <Input
                  value={form.tagline}
                  onChange={(e) => updateForm('tagline', e.target.value)}
                  placeholder='e.g. "Think Different" or "Just Do It"'
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Mission Statement</label>
                <textarea
                  value={form.mission_statement}
                  onChange={(e) => updateForm('mission_statement', e.target.value)}
                  placeholder="What the brand exists to do..."
                  rows={2}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Personality Traits</label>
                <TagInput
                  value={form.brand_personality}
                  onChange={(v) => updateForm('brand_personality', v)}
                  placeholder="e.g. innovative, trustworthy, playful"
                  colorClass="bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Core Values</label>
                <TagInput
                  value={form.brand_values}
                  onChange={(v) => updateForm('brand_values', v)}
                  placeholder="e.g. excellence, creativity, integrity"
                  colorClass="bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Target Audience</label>
                <textarea
                  value={form.target_audience}
                  onChange={(e) => updateForm('target_audience', e.target.value)}
                  placeholder="Who the brand speaks to..."
                  rows={2}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
            </div>
          </div>

          {/* ── Tone Calibration (Multi-axis) ── */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Sliders className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-foreground">Tone Calibration</h3>
            </div>
            <ToneScaleSlider
              scales={form.tone_scales}
              onChange={(v) => updateForm('tone_scales', v)}
            />
          </div>

          {/* ── Content & Vocabulary ── */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-foreground">Content & Vocabulary</h3>
            </div>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Content Guidelines</label>
                <textarea
                  value={form.content_guidelines}
                  onChange={(e) => updateForm('content_guidelines', e.target.value)}
                  placeholder="Messaging dos and don'ts, key messages, value propositions..."
                  rows={3}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Preferred Words & Phrases</label>
                <TagInput
                  value={form.words_to_use}
                  onChange={(v) => updateForm('words_to_use', v)}
                  placeholder="Words to use — type and press Enter"
                  colorClass="bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Banned Words & Phrases</label>
                <TagInput
                  value={form.words_to_avoid}
                  onChange={(v) => updateForm('words_to_avoid', v)}
                  placeholder="Words to avoid — type and press Enter"
                  colorClass="bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20"
                />
              </div>
            </div>
          </div>

          {/* ── Visual Style ── */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-foreground">Visual Style</h3>
            </div>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Visual Mood / Aesthetic</label>
                <Input
                  value={form.visual_mood}
                  onChange={(e) => updateForm('visual_mood', e.target.value)}
                  placeholder="e.g. bold, minimalist, warm, tech-forward, corporate classic"
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Style Guidelines</label>
                <textarea
                  value={form.style_guidelines}
                  onChange={(e) => updateForm('style_guidelines', e.target.value)}
                  placeholder="Layout preferences, whitespace rules, image style preferences..."
                  rows={3}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Design Elements</label>
                <DesignElementsEditor
                  elements={form.design_elements}
                  onChange={(v) => updateForm('design_elements', v)}
                />
              </div>
            </div>
          </div>

          {/* ── Logo ── */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Image className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-foreground">Logo</h3>
            </div>

            {/* r95: logos found IN the ingested files — one click to adopt.
                The candidate becomes a real File through the existing
                upload path, so storage/variants behave exactly as manual */}
            {logoCandidates.length > 0 && (
              <div className="mb-4 p-3 rounded-lg border border-primary/25 bg-primary/5">
                <p className="text-xs font-medium text-foreground mb-2">
                  Logos found in your files — click to use:
                </p>
                <div className="flex gap-4 flex-wrap">
                  {logoCandidates.map((c, i) => {
                    const SET = {
                      primary: [setLogoFile, setLogoPreview],
                      reversed: [setLogoReversedFile, setLogoReversedPreview],
                      icon: [setLogoIconFile, setLogoIconPreview],
                    }[c.variant] || [setLogoFile, setLogoPreview];
                    return (
                      <div key={i} className="text-center space-y-1">
                        <img src={c.data_url} alt={`candidate ${i + 1}`}
                          className="h-12 max-w-[130px] object-contain rounded border border-border bg-white p-1 mx-auto" />
                        <button type="button"
                          className="px-2 py-1 rounded text-[11px] border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                          onClick={async () => {
                            try {
                              const blob = await (await fetch(c.data_url)).blob();
                              const file = new File([blob], `logo-${c.variant}.png`, { type: 'image/png' });
                              SET[0](file);
                              SET[1](c.data_url);
                              toast.success(`Set as ${c.variant} logo`);
                            } catch { toast.error('Could not use this candidate'); }
                          }}>
                          Use as {c.variant}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Three logo variants */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: 'Primary', desc: 'Full color, light bg', preview: logoPreview, setter: setLogoFile, previewSetter: setLogoPreview },
                { label: 'Reversed', desc: 'Light, dark bg', preview: logoReversedPreview, setter: setLogoReversedFile, previewSetter: setLogoReversedPreview },
                { label: 'Icon / Mark', desc: 'Square, small use', preview: logoIconPreview, setter: setLogoIconFile, previewSetter: setLogoIconPreview },
              ].map((variant) => (
                <div key={variant.label} className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">{variant.label}</label>
                  <p className="text-[10px] text-muted-foreground">{variant.desc}</p>
                  <label
                    className="cursor-pointer block"
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => {
                      e.preventDefault(); e.stopPropagation();
                      const file = e.dataTransfer?.files?.[0];
                      if (file && file.type?.startsWith('image/')) {
                        handleFileChange(variant.setter, variant.previewSetter)({ target: { files: [file], value: '' } });
                      }
                    }}>
                    <div className={`w-full aspect-[3/2] rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden transition-colors ${
                      variant.preview ? 'border-border bg-muted' : 'border-border hover:border-primary/30 bg-muted/30'
                    }`}>
                      {variant.preview ? (
                        <img src={variant.preview} alt="" className="w-full h-full object-contain p-2" />
                      ) : (
                        <Upload className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                    <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={handleFileChange(variant.setter, variant.previewSetter)} className="sr-only" />
                  </label>
                </div>
              ))}
            </div>

            {/* r170: logo usage as prose — the source of truth. The dropdowns
                below are derived from this text on save (still overridable
                when the text is left unchanged). */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">How does this brand use its logo?</label>
              <textarea
                value={form.logo_usage}
                onChange={(e) => updateForm('logo_usage', e.target.value)}
                rows={3}
                placeholder={'Describe it the way the brand guidelines do, e.g. "Full wordmark centered on the cover; small icon-only mark top-right on content slides; reversed (white) version on dark backgrounds; never wider than a tenth of the slide."'}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y"
              />
              <p className="text-[11px] text-muted-foreground">
                The placement settings below are derived from this description when you save. Editing them directly still works while the description is unchanged.
              </p>
            </div>

            {/* r181: company fact sheet — the authoritative tier for proposals.
                Used ONLY when a proposal is generated with this brand. */}
            <div className="space-y-2 pt-4 border-t border-border">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Company fact sheet (proposals only)</label>
                  <p className="text-[11px] text-muted-foreground">
                    Approved facts a proposal may state as true: certificates, team bios, rate cards, terms, methodology, case studies. Unapproved or expired entries are never used; an expired entry becomes a placeholder instead.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, fact_sheet: [...(f.fact_sheet || []), fsNewEntry()] }))}
                  className="shrink-0 text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-muted transition-colors"
                >
                  Add fact
                </button>
              </div>
              {(form.fact_sheet || []).map((e, idx) => (
                <div key={e.id || idx} className={`rounded-lg border p-3 space-y-2 ${e.approved ? 'border-border' : 'border-amber-300/70 dark:border-amber-700/60 bg-amber-50/30 dark:bg-amber-950/10'}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <select value={e.category} onChange={(ev) => updateFsEntry(idx, { category: ev.target.value })} className="rounded-md border border-input bg-background px-2 py-1.5 text-xs">
                      {FS_CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                    </select>
                    <input value={e.title || ''} onChange={(ev) => updateFsEntry(idx, { title: ev.target.value })} placeholder="Title (e.g. ISO 27001, Sara Al-Hashimi)" className="rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    <select value={e.disclosure || 'nameable'} onChange={(ev) => updateFsEntry(idx, { disclosure: ev.target.value })} className="rounded-md border border-input bg-background px-2 py-1.5 text-xs">
                      <option value="nameable">Client may be named</option>
                      <option value="anonymize">Anonymize the client</option>
                    </select>
                  </div>
                  <textarea value={e.body || ''} onChange={(ev) => updateFsEntry(idx, { body: ev.target.value })} rows={2} placeholder="The fact, exactly as a proposal may state it" className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs resize-y" />
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground">
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={!!e.approved} onChange={(ev) => updateFsEntry(idx, { approved: ev.target.checked })} />
                      <span className={e.approved ? 'text-foreground font-medium' : ''}>Approved</span>
                    </label>
                    <label className="inline-flex items-center gap-1.5">Last verified
                      <input type="date" value={e.last_verified || ''} onChange={(ev) => updateFsEntry(idx, { last_verified: ev.target.value })} className="rounded border border-input bg-background px-1.5 py-0.5 text-[11px]" />
                    </label>
                    <label className="inline-flex items-center gap-1.5">Expires
                      <input type="date" value={e.expires || ''} onChange={(ev) => updateFsEntry(idx, { expires: ev.target.value })} className="rounded border border-input bg-background px-1.5 py-0.5 text-[11px]" />
                    </label>
                    <button type="button" onClick={() => removeFsEntry(idx)} className="ml-auto text-[11px] text-destructive hover:underline">Remove</button>
                  </div>
                </div>
              ))}
              <div className="space-y-1.5">
                <textarea
                  value={fsSeedText}
                  onChange={(ev) => setFsSeedText(ev.target.value)}
                  rows={2}
                  placeholder="Paste text from a past proposal and we will draft facts for you to review"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                />
                <button
                  type="button"
                  disabled={fsSeeding || !fsSeedText.trim()}
                  onClick={seedFactSheet}
                  className="text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-muted transition-colors disabled:opacity-50"
                >
                  {fsSeeding ? 'Drafting facts' : 'Draft facts for review'}
                </button>
              </div>
            </div>

            {/* r182: past proposals — the reference tier. Each upload becomes one
                structured card (team, pricing as written, voice register); a
                proposal generated with this brand sees the most relevant ones,
                framed as previous clients' facts. */}
            <div className="space-y-2 pt-4 border-t border-border">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Past proposals (reference library, proposals only)</label>
                <p className="text-[11px] text-muted-foreground">
                  Upload previous proposals (PDF, Word, PowerPoint). Each becomes a card of team, pricing as written, structure and voice. New proposals see the most relevant cards, framed as previous clients' facts, never as your own.
                </p>
              </div>
              {!identity?.id ? (
                <p className="text-[11px] text-muted-foreground italic">Save this brand first, then upload past proposals here.</p>
              ) : (
                <>
                  <label
                    className={`flex items-center justify-center gap-2 rounded-lg border border-dashed border-border px-3 py-3 text-xs text-muted-foreground cursor-pointer hover:bg-muted/50 transition-colors ${pcUploading ? 'opacity-60 pointer-events-none' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (!pcUploading && e.dataTransfer?.files?.length) uploadProposalCards(e.dataTransfer.files); }}
                  >
                    {pcUploading ? 'Reading and extracting' : 'Drop past proposals here, or click to choose'}
                    <input type="file" multiple accept=".pdf,.docx,.pptx,.txt,.md" className="sr-only" onChange={(e) => { uploadProposalCards(e.target.files); e.target.value = ''; }} />
                  </label>
                  {pcCards.length > 0 && (
                    <ul className="space-y-1">
                      {pcCards.map(c => (
                        <li key={c.id} className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-xs">
                          <div className="flex-1 min-w-0">
                            <p className="truncate text-foreground">
                              <span className="font-medium">{c.client || 'Client not named'}</span>
                              {c.engagement_title ? ` - ${c.engagement_title}` : ''}
                            </p>
                            <p className="truncate text-[11px] text-muted-foreground">
                              {[c.engagement_type, c.sector, c.language, c.price_lines ? `${c.price_lines} price line${c.price_lines === 1 ? '' : 's'}` : null, c.team_size ? `${c.team_size} team` : null].filter(Boolean).join(' / ')}
                              {c.stale ? ' / re-upload to refresh' : ''}
                            </p>
                          </div>
                          <button type="button" onClick={() => removeProposalCard(c.id)} className="text-[11px] text-destructive hover:underline shrink-0">Remove</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>

            {/* Placement settings */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Position</label>
                <select
                  value={form.logo_position}
                  onChange={(e) => updateForm('logo_position', e.target.value)}
                  className="w-full h-9 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {LOGO_POSITIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Max Width: {form.logo_max_width}px</label>
                <input
                  type="range"
                  min={80}
                  max={300}
                  value={form.logo_max_width}
                  onChange={(e) => updateForm('logo_max_width', parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Show On</label>
                <select
                  value={form.logo_show_on}
                  onChange={(e) => updateForm('logo_show_on', e.target.value)}
                  className="w-full h-9 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {LOGO_SHOW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* ── Reference Doc ── */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Brand Reference Document</label>
            <div className="flex items-center gap-4">
              {refDocName ? (
                <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-lg px-3 py-2">
                  <FileText className="w-4 h-4 text-primary" />
                  <span className="text-sm text-foreground">{refDocName}</span>
                  <button onClick={() => { setRefDocFile(null); setRefDocName(null); }} className="text-muted-foreground hover:text-destructive">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <label
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    const file = e.dataTransfer?.files?.[0];
                    if (file && /\.pdf$/i.test(file.name)) { setRefDocFile(file); setRefDocName(file.name); }
                  }}
                  className="cursor-pointer flex items-center gap-2 border-2 border-dashed border-border rounded-lg px-4 py-3 hover:border-primary/30 transition-colors">
                  <Upload className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Upload PDF brand guidelines</span>
                  <input type="file" accept=".pdf" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) { setRefDocFile(file); setRefDocName(file.name); }
                  }} className="sr-only" />
                </label>
              )}
              {refDocName && !refDocFile && identity?.reference_doc_url && (
                <a
                  href={identity.reference_doc_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary underline hover:no-underline"
                >
                  Open document
                </a>
              )}
            </div>
            {/* r142: what the system actually HOLDS from the uploaded
                guidelines — users had no way to verify their instructions
                were read. Shown only for the stored doc, not a pending
                re-upload (whose text does not exist yet). */}
            {!refDocFile && identity?.reference_doc_text && (
              <details className="rounded-lg border border-border bg-muted/20 px-3 py-2">
                <summary className="text-xs font-medium text-foreground cursor-pointer select-none">
                  What was read from this document ({(identity.reference_doc_text.length).toLocaleString()} characters)
                </summary>
                <p className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap max-h-64 overflow-y-auto">
                  {identity.reference_doc_text}
                </p>
              </details>
            )}
          </div>

          {/* ── Reference Slides ── */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Reference Slides</label>
            <p className="text-xs text-muted-foreground">Upload a PPTX or PDF with sample slides. The AI will study and replicate the visual style, layout patterns, and design language when generating with this brand.</p>
            <div className="flex items-center gap-2">
              {refSlidesFile ? (
                <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-lg px-3 py-2">
                  <FileText className="w-4 h-4 text-primary" />
                  <span className="text-sm text-foreground">{refSlidesFile.name}</span>
                  <button onClick={() => setRefSlidesFile(null)} disabled={saving} className="text-muted-foreground hover:text-destructive disabled:opacity-40">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <label
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    const file = e.dataTransfer?.files?.[0];
                    if (file && /\.(pptx|ppt|pdf)$/i.test(file.name)) setRefSlidesFile(file);
                  }}
                  className="cursor-pointer flex items-center gap-2 border-2 border-dashed border-border rounded-lg px-4 py-3 hover:border-primary/30 transition-colors">
                  <Upload className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{refSlidesPreviews.length > 0 ? 'Replace reference slides' : 'Upload PPTX or PDF with sample slides'}</span>
                  <input type="file" accept=".pptx,.ppt,.pdf" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setRefSlidesFile(file);
                  }} className="sr-only" />
                </label>
              )}
              {refSlidesPreviews.length > 0 && !refSlidesFile && (
                <button onClick={() => { setRefSlidesPreviews([]); setRefSlidesFile(null); }} className="text-xs text-muted-foreground hover:text-destructive underline">
                  Clear all
                </button>
              )}
            </div>
            {/* Stored slide screenshots — these were previously saved and
                used by generation but never shown, which read as "my upload
                disappeared". Each item is {index, title, preview_url}. */}
            {refSlidesPreviews.length > 0 && !refSlidesFile && (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 pt-2">
                {refSlidesPreviews.map((p, i) => {
                  const url = typeof p === 'string' ? p : (p?.preview_url || '');
                  if (!url) return null;
                  return (
                    <div key={i} className="relative group rounded-md border border-border overflow-hidden bg-muted/30" title={(typeof p === 'object' && p?.title) || `Slide ${i + 1}`}>
                      <img src={url} alt={`Reference slide ${i + 1}`} loading="lazy" className="w-full aspect-video object-cover" />
                      <span className="absolute bottom-0.5 right-1 text-[10px] font-medium text-white bg-black/50 rounded px-1">{i + 1}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Brand Shapes (vector motifs harvested from PPTX uploads) ── */}
          {form.brand_motifs.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Brand Shapes</label>
              <p className="text-xs text-muted-foreground">
                Decorative motifs auto-extracted from your reference slides' vector shapes. Generation
                places these exact assets instead of improvising decoration — remove any that aren't
                really part of the brand.
              </p>
              <div className="flex flex-wrap gap-2">
                {form.brand_motifs.map((m, i) => (
                  <div key={i} className="relative group rounded-lg border border-border bg-muted/30 p-2 w-24 h-20 flex items-center justify-center overflow-hidden">
                    {/* Rendered as an <img> data URL on purpose: SVG in image
                        context cannot execute scripts. Motifs are normally
                        server-generated, but shared brands copy this field
                        from OTHER users' brands — never innerHTML it. */}
                    <img
                      src={`data:image/svg+xml;utf8,${encodeURIComponent(m.svg || '')}`}
                      alt={`Brand shape ${i + 1}`}
                      className="max-w-full max-h-14 w-auto h-auto"
                    />
                    <button
                      type="button"
                      onClick={() => updateForm('brand_motifs', form.brand_motifs.filter((_, j) => j !== i))}
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity rounded bg-background/80 p-0.5 text-muted-foreground hover:text-destructive"
                      title="Remove this shape"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    {m.freq > 1 && (
                      <span className="absolute bottom-0.5 right-1 text-[9px] text-muted-foreground">×{m.freq}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Layout & Composition Patterns ── */}
          {form.design_patterns && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Layout className="w-4 h-4 text-primary" />
                Layout & Composition Patterns
              </label>
              <p className="text-xs text-muted-foreground">Auto-extracted from reference slides. Edit to fine-tune how slides are composed.</p>
              <textarea
                value={form.design_patterns}
                onChange={(e) => updateForm('design_patterns', e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground min-h-[120px] resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Layout patterns will appear here after uploading reference slides..."
              />
            </div>
          )}

          {/* ── Save ── */}
          {/* Heavy-processing banner: a reference deck goes through
              LibreOffice conversion, per-slide screenshots, motif harvesting
              and a vision analysis — 30-90s during which "Saving..." alone
              reads as a hang. */}
          {saving && refSlidesFile && (
            <div className="flex items-start gap-3 p-3 rounded-xl border border-accent/30 bg-accent/5">
              <span className="mt-0.5 w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">Processing your reference deck…</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Converting slides, capturing previews, harvesting brand shapes, and analyzing layout
                  patterns with a vision model. This usually takes 30–90 seconds — keep this tab open.
                </p>
              </div>
            </div>
          )}
          {identity?.id && <BrandFeedbackSection identityId={identity.id} />}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
            <Button variant="gradient" onClick={handleSubmit} disabled={saving}>
              {saving
                ? (refSlidesFile ? 'Processing reference deck…' : 'Saving...')
                : (identity?.id ? 'Update' : 'Create Brand Identity')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Team Share Modal ────────────────────────────────────────────────────────
const MEMBER_ROLES = [
  { value: 'viewer', label: 'Viewer', desc: 'Can use in generations' },
  { value: 'editor', label: 'Editor', desc: 'Can also edit the brand' },
];

function TeamShareModal({ brand, userId, onClose }) {
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviteError, setInviteError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [updatingMemberId, setUpdatingMemberId] = useState(null);
  const [removingMemberId, setRemovingMemberId] = useState(null);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await fetch(withBackend(`/api/brand-identities/${brand.id}/members?user_id=${userId}`));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load members');
      setMembers(data.members || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load members');
    } finally {
      setLoadingMembers(false);
    }
  }, [brand.id, userId]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleAdd = async () => {
    const email = inviteEmail.trim();
    if (!email || adding) return;
    setAdding(true);
    setInviteError(null);
    try {
      const res = await fetch(withBackend(`/api/brand-identities/${brand.id}/members`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, email, role: inviteRole }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setInviteError(data.error || 'Failed to add member');
        return;
      }
      setInviteEmail('');
      toast.success(`Shared with ${email}`);
      fetchMembers();
    } catch (err) {
      setInviteError('Something went wrong — please try again');
      toast.error(err.message || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const handleRoleChange = async (member, role) => {
    if (role === member.role) return;
    setUpdatingMemberId(member.id);
    try {
      const res = await fetch(withBackend(`/api/brand-identities/${brand.id}/members/${member.id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, role }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update role');
      setMembers(prev => prev.map(m => (m.id === member.id ? { ...m, ...(data.member || { role }) } : m)));
      toast.success(`${member.email} is now ${role === 'editor' ? 'an editor' : 'a viewer'}`);
    } catch (err) {
      toast.error(err.message || 'Failed to update role');
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemove = async (member) => {
    setRemovingMemberId(member.id);
    try {
      const res = await fetch(withBackend(`/api/brand-identities/${brand.id}/members/${member.id}?user_id=${userId}`), { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to remove member');
      setMembers(prev => prev.filter(m => m.id !== member.id));
      toast.success(`Removed ${member.email}`);
    } catch (err) {
      toast.error(err.message || 'Failed to remove member');
    } finally {
      setRemovingMemberId(null);
    }
  };

  const selectedRole = MEMBER_ROLES.find(r => r.value === inviteRole);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-2xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-2 min-w-0">
            <Users className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-foreground">Share {brand.name} with your team</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Teammates see and use this brand from their own account. Changes you make stay in sync.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg transition-colors flex-shrink-0">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Add member row */}
        <div className="space-y-1.5 mb-4">
          <div className="flex gap-2">
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => { setInviteEmail(e.target.value); if (inviteError) setInviteError(null); }}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder="teammate@company.com"
              className="flex-1 h-9 text-sm"
              disabled={adding}
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              disabled={adding}
              className="h-9 rounded-lg border border-input bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            >
              {MEMBER_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <Button size="sm" className="h-9 px-4" onClick={handleAdd} disabled={adding || !inviteEmail.trim()}>
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
            </Button>
          </div>
          {inviteError ? (
            <p className="text-xs text-destructive">{inviteError}</p>
          ) : (
            <p className="text-[10px] text-muted-foreground">{selectedRole?.label}: {selectedRole?.desc}</p>
          )}
        </div>

        {/* Member list */}
        <div className="border border-border rounded-lg divide-y divide-border max-h-60 overflow-y-auto">
          {loadingMembers ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
            </div>
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Not shared with anyone yet.</p>
          ) : (
            members.map((m) => (
              <div key={m.id} className="flex items-center gap-2 px-3 py-2">
                <span className="flex-1 min-w-0 text-sm text-foreground truncate" title={m.email}>{m.email}</span>
                {updatingMemberId === m.id && <Loader2 className="w-3 h-3 text-muted-foreground animate-spin flex-shrink-0" />}
                <select
                  value={m.role}
                  onChange={(e) => handleRoleChange(m, e.target.value)}
                  disabled={updatingMemberId === m.id || removingMemberId === m.id}
                  className="h-7 rounded-md border border-input bg-background px-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                >
                  {MEMBER_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <button
                  onClick={() => handleRemove(m)}
                  disabled={removingMemberId === m.id || updatingMemberId === m.id}
                  className="p-1 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                  title="Remove access"
                >
                  {removingMemberId === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer note */}
        <p className="text-[10px] text-muted-foreground mt-4">
          Need to send it outside your team? Use the copy-link button on the card to share a one-time copy.
        </p>
      </div>
    </div>
  );
}

// ─── List Page ───────────────────────────────────────────────────────────────
export function BrandIdentitiesPage({ userId, onBack }) {
  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [shareModalBrand, setShareModalBrand] = useState(null);
  const [confirmLeaveId, setConfirmLeaveId] = useState(null);
  const [leavingId, setLeavingId] = useState(null);

  const fetchIdentities = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await fetch(withBackend(`/api/brand-identities?user_id=${userId}`));
      const data = await res.json();
      setIdentities(data.brand_identities || []);
    } catch (err) {
      toast.error('Failed to load brand identities');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchIdentities(); }, [fetchIdentities]);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      const res = await fetch(withBackend(`/api/brand-identities/${id}?user_id=${encodeURIComponent(userId || '')}`), { method: 'DELETE', headers: await authHeaders() });
      if (!res.ok) throw new Error('Delete failed');
      setIdentities(prev => prev.filter(i => i.id !== id));
      toast.success('Brand identity deleted');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleSave = () => {
    setEditingId(null);
    fetchIdentities();
  };

  const handleSetDefault = async (id) => {
    const bi = identities.find(i => i.id === id);
    const isCurrentDefault = bi?.is_default;
    try {
      if (isCurrentDefault) {
        await fetch(withBackend(`/api/brand-identities/${id}/unset-default`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId }),
        });
        toast.success('Default removed');
      } else {
        await fetch(withBackend(`/api/brand-identities/${id}/set-default`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, auto_apply: false }),
        });
        toast.success('Set as default brand');
      }
      fetchIdentities();
    } catch (err) {
      toast.error('Failed to update default');
    }
  };

  const handleToggleAutoApply = async (id) => {
    const bi = identities.find(i => i.id === id);
    if (!bi?.is_default) return;
    const newAutoApply = !bi.auto_apply;
    try {
      await fetch(withBackend(`/api/brand-identities/${id}/set-default`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, auto_apply: newAutoApply }),
      });
      toast.success(newAutoApply ? 'Auto-apply enabled' : 'Auto-apply disabled');
      fetchIdentities();
    } catch (err) {
      toast.error('Failed to update setting');
    }
  };

  const handleShare = async (id) => {
    try {
      const resp = await fetch(withBackend(`/api/brand-identities/${id}/share`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      if (!resp.ok) throw new Error('Failed to create share link');
      const { share_token, brand_name } = await resp.json();
      // Use the canonical production domain for outbound share links —
      // we don't want recipients landing on the old prezlab-brain.netlify.app
      // host. Keeps localhost links working for dev.
      const origin = window.location.origin || '';
      const canonical = /prezlab-brain\.netlify\.app$/i.test(new URL(origin).hostname)
        ? 'https://getwizardly.ai'
        : origin;
      const shareUrl = `${canonical}/brand/share/${share_token}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success(`Share link for "${brand_name}" copied to clipboard!`);
    } catch (err) {
      toast.error(err.message || 'Failed to share brand identity');
    }
  };

  // Leave a brand that was shared with the current user: look up our own
  // member row id via GET members, then DELETE it.
  const handleLeave = async (bi) => {
    setLeavingId(bi.id);
    try {
      const res = await fetch(withBackend(`/api/brand-identities/${bi.id}/members?user_id=${userId}`));
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load membership');
      const myRow = (data.members || []).find(m => m.user_id === userId);
      if (!myRow) throw new Error('Could not find your membership for this brand');
      const del = await fetch(withBackend(`/api/brand-identities/${bi.id}/members/${myRow.id}?user_id=${userId}`), { method: 'DELETE' });
      const delData = await del.json().catch(() => ({}));
      if (!del.ok) throw new Error(delData.error || 'Failed to leave brand');
      toast.success(`You left "${bi.name}"`);
      setConfirmLeaveId(null);
      fetchIdentities();
    } catch (err) {
      toast.error(err.message || 'Failed to leave brand');
    } finally {
      setLeavingId(null);
    }
  };

  // Resolve display colors — prefer semantic, fall back to legacy
  const getDisplayColors = (bi) => {
    const semantic = [bi.color_background, bi.color_surface, bi.color_text_primary, bi.color_text_body, bi.color_accent, bi.color_border].filter(Boolean);
    if (semantic.length >= 2) return semantic;
    return [bi.primary_color, bi.secondary_color, bi.accent_color].filter(Boolean);
  };

  // Edit/Create form view
  if (editingId) {
    const editing = editingId === 'new' ? null : identities.find(i => i.id === editingId);
    return (
      <div className="min-h-screen py-12 px-6">
        <BrandIdentityForm
          identity={editing}
          onSave={handleSave}
          onCancel={() => setEditingId(null)}
          userId={userId}
        />
      </div>
    );
  }

  // List view
  return (
    <div className="min-h-screen max-w-5xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="icon" onClick={onBack} className="mr-1">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-foreground">Brand Identities</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage reusable brand profiles for consistent generation.</p>
          </div>
        </div>
        <Button variant="gradient" onClick={() => setEditingId('new')}>
          <Plus className="w-4 h-4 mr-2" /> New Brand Identity
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : identities.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Palette className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No brand identities yet</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Create your first brand identity to use across all your generations.
          </p>
          <Button variant="gradient" onClick={() => setEditingId('new')}>
            <Plus className="w-4 h-4 mr-2" /> Create Brand Identity
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {identities.map((bi) => {
            const colors = getDisplayColors(bi);
            const isShared = bi.is_shared === true;
            return (
              <Card key={bi.id} className="group hover:shadow-card-hover hover:border-primary/20 overflow-hidden flex flex-col h-full">
                {/* Color bar */}
                <div className="h-2 flex">
                  {colors.length > 0 ? colors.map((c, i) => (
                    <div key={i} className="flex-1" style={{ backgroundColor: c }} />
                  )) : (
                    <div className="flex-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
                  )}
                </div>

                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {bi.logo_url ? (
                        <div className="w-10 h-10 rounded-lg border border-border bg-muted overflow-hidden flex-shrink-0">
                          <img src={bi.logo_url} alt="" className="w-full h-full object-contain p-0.5" />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bi.color_accent || bi.primary_color || '#6366F1' }}>
                          <span className="text-white font-bold text-sm">{(bi.name || 'B')[0].toUpperCase()}</span>
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="font-semibold text-foreground leading-snug line-clamp-2" title={bi.name}>{bi.name}</h3>
                        <p className="text-xs text-muted-foreground">
                          {[bi.font_heading, bi.font_body].filter(Boolean).join(' / ') || 'No fonts set'}
                        </p>
                      </div>
                    </div>
                    {isShared && (
                      <div className="flex flex-col items-end gap-0.5 flex-shrink-0 ml-2">
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-accent text-accent-foreground border border-border">
                          <Users className="w-3 h-3" /> Shared
                        </span>
                        {bi.shared_by && (
                          <span className="text-[10px] text-muted-foreground truncate max-w-[120px]" title={`Shared by ${bi.shared_by}`}>
                            by {bi.shared_by}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Tagline */}
                  {bi.tagline && (
                    <p className="text-xs text-primary font-medium mb-1 truncate">"{bi.tagline}"</p>
                  )}

                  {/* Tone summary */}
                  {(bi.tone_formality || bi.tone_of_voice) && (
                    <p className="text-xs text-muted-foreground line-clamp-1 mb-1 italic">
                      {bi.tone_formality && bi.tone_perspective
                        ? `${bi.tone_formality} / ${bi.tone_perspective} person / ${bi.tone_complexity || 'accessible'}`
                        : bi.tone_of_voice
                          ? `"${bi.tone_of_voice}"`
                          : ''}
                    </p>
                  )}

                  {/* Personality & values badges */}
                  {((bi.brand_personality && bi.brand_personality.length > 0) || (bi.brand_values && bi.brand_values.length > 0)) && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(bi.brand_personality || []).slice(0, 3).map((t, i) => (
                        <span key={`p-${i}`} className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-600 dark:text-purple-400">{t}</span>
                      ))}
                      {(bi.brand_values || []).slice(0, 2).map((v, i) => (
                        <span key={`v-${i}`} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400">{v}</span>
                      ))}
                    </div>
                  )}

                  {/* Target audience */}
                  {bi.target_audience && (
                    <p className="text-[10px] text-muted-foreground mb-2 line-clamp-1">
                      <Target className="w-3 h-3 inline mr-1" />{bi.target_audience}
                    </p>
                  )}

                  {/* Color swatches */}
                  <div className="flex flex-wrap gap-1.5 gap-y-1.5 mb-4 mt-auto">
                    {colors.slice(0, 6).map((c, i) => (
                      <div key={i} className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded-full border border-border/50" style={{ backgroundColor: c }} />
                        <span className="text-[10px] text-muted-foreground font-mono">{c}</span>
                      </div>
                    ))}
                  </div>

                  {/* Default + Auto-apply (owned brands only) */}
                  {!isShared && (
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      onClick={() => handleSetDefault(bi.id)}
                      className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-all ${
                        bi.is_default
                          ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'
                          : 'text-muted-foreground hover:bg-muted border border-transparent'
                      }`}
                    >
                      <Star className={`w-3.5 h-3.5 ${bi.is_default ? 'fill-current' : ''}`} />
                      {bi.is_default ? 'Default' : 'Set default'}
                    </button>
                    {bi.is_default && (
                      <button
                        onClick={() => handleToggleAutoApply(bi.id)}
                        className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-all ${
                          bi.auto_apply
                            ? 'bg-primary/10 text-primary border border-primary/20'
                            : 'text-muted-foreground hover:bg-muted border border-transparent'
                        }`}
                      >
                        <Zap className={`w-3.5 h-3.5 ${bi.auto_apply ? 'fill-current' : ''}`} />
                        {bi.auto_apply ? 'Auto-apply on' : 'Auto-apply'}
                      </button>
                    )}
                  </div>
                  )}

                  {/* Actions */}
                  {isShared ? (
                    confirmLeaveId === bi.id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground flex-1">Leave this brand?</span>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmLeaveId(null)} disabled={leavingId === bi.id}>
                          Cancel
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleLeave(bi)}
                          disabled={leavingId === bi.id}
                        >
                          {leavingId === bi.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Leave'}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        {bi.shared_role === 'editor' && (
                          <Button variant="outline" size="sm" className="flex-1" onClick={() => setEditingId(bi.id)}>
                            <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Edit
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`text-muted-foreground hover:text-destructive hover:bg-destructive/10 ${bi.shared_role === 'editor' ? '' : 'flex-1'}`}
                          onClick={() => setConfirmLeaveId(bi.id)}
                          title="Leave this shared brand"
                        >
                          <LogOut className="w-3.5 h-3.5 mr-1.5" /> Leave
                        </Button>
                      </div>
                    )
                  ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => setEditingId(bi.id)}>
                      <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-primary hover:text-primary hover:bg-primary/10"
                      onClick={() => setShareModalBrand(bi)}
                      title="Share with team"
                    >
                      <Users className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-primary hover:text-primary hover:bg-primary/10"
                      onClick={() => handleShare(bi.id)}
                      title="Copy share link"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleDelete(bi.id)}
                      disabled={deletingId === bi.id}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Team sharing modal */}
      {shareModalBrand && (
        <TeamShareModal
          brand={shareModalBrand}
          userId={userId}
          onClose={() => setShareModalBrand(null)}
        />
      )}
    </div>
  );
}
